import type { Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useErrorStore } from '@/stores/errors';

/**
 * The fields shown on each tab of a form, keyed by the tab's value, in the order the tabs
 * are displayed. Fields shown outside the tabs are left out.
 */
export type TabFields = Record<string, string[]>;

/**
 * The tabs holding at least one of the given validation errors, in display order.
 * Nested keys such as `widget_config.0.key` count towards the tab of their top-level field.
 */
export function tabsWithErrors(tabFields: TabFields, errors: Record<string, unknown>): string[] {
  const fieldsWithErrors = new Set(Object.keys(errors).map(key => key.split('.')[0]));

  return Object.keys(tabFields)
    .filter(tab => tabFields[tab].some(field => fieldsWithErrors.has(field)));
}

/**
 * Tracks which tabs of a form hold a field with a validation error, so each `<smpw-tab>`
 * can be given `:has-errors="erroredTabs.includes('name')"`.
 *
 * Call `openErroredTab()` when saving fails (`ModelEditionForm` emits `save:failed`): it
 * opens the first tab with an error, unless the open tab has one itself.
 */
export function useTabErrors(tabFields: TabFields, tab: Ref<string>) {
  const { validationErrors } = storeToRefs(useErrorStore());
  const erroredTabs = computed(() => tabsWithErrors(tabFields, validationErrors.value));

  function openErroredTab(): void {
    if (erroredTabs.value.length > 0 && !erroredTabs.value.includes(tab.value)) {
      tab.value = erroredTabs.value[0];
    }
  }

  return { erroredTabs, openErroredTab };
}
